'use client';

import { Popover as PopoverPrimitive } from '@base-ui/react/popover';

import { cn } from './utils';

/**
 * Click-triggered popover, on @base-ui/react's Popover — the same primitive
 * library the rest of this package is built on, so the portal, outside-click
 * dismissal and focus return all come from the primitive.
 *
 * Click-triggered, not hover: a tooltip explains an affordance to someone with
 * a pointer, and this exists for the cases where an action is *unavailable* —
 * which someone on a touch device needs to be able to ask about too.
 *
 * PopoverContent bundles Portal + Positioner + Popup for the same reason
 * MenuContent does: a popup rendered outside a Positioner is unanchored, and
 * every call site wants the same three layers.
 */
function Popover(props: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root {...props} />;
}

function PopoverTrigger({
  className,
  ...props
}: PopoverPrimitive.Trigger.Props) {
  return (
    <PopoverPrimitive.Trigger
      className={cn(
        'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        className,
      )}
      data-slot="popover-trigger"
      {...props}
    />
  );
}

function PopoverContent({
  align = 'center',
  className,
  side = 'top',
  sideOffset = 8,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, 'align' | 'side' | 'sideOffset'>) {
  return (
    <PopoverPrimitive.Portal>
      {/* Above anything the page stacks — the feedback panel floats on the
          board in a z-10 layer, and an unlayered popup portalled to <body>
          painted behind it: "End session" opened its confirm out of sight. */}
      <PopoverPrimitive.Positioner
        align={align}
        className="z-50"
        side={side}
        sideOffset={sideOffset}
      >
        <PopoverPrimitive.Popup
          className={cn(
            'max-w-72 origin-(--transform-origin) rounded-xl border border-border bg-popover p-3 text-sm text-popover-foreground shadow-md outline-none transition-[opacity,transform] duration-150 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0',
            className,
          )}
          data-slot="popover-content"
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

export { Popover, PopoverContent, PopoverTrigger };
